<template>
  <div class="p-4 md:p-6">
    <!-- Back button + Header -->
    <div class="flex items-center gap-3 mb-6 md:mb-8">
      <Button
        icon="pi pi-arrow-right"
        text
        rounded
        class="!text-gray-400 hover:!text-gray-900 hover:!bg-gray-100 flex-shrink-0"
        @click="router.push({ name: PlatformRouteNames.Activity })"
      />
      <div v-if="data" class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <h1 class="text-xl md:text-2xl font-bold text-gray-900 truncate">{{ data.business.title }}</h1>
          <Tag
            v-if="data.business.isTestBusiness"
            value="בדיקה"
            severity="warn"
            class="!text-xs flex-shrink-0"
          />
        </div>
        <p class="text-xs md:text-sm text-gray-400 truncate">{{ data.business.bid }} | {{ data.business.phone }}</p>
      </div>
      <div v-if="data" class="flex items-center gap-2 flex-shrink-0">
        <Button
          label="אתר העסק"
          icon="pi pi-external-link"
          outlined
          class="!rounded-xl"
          @click="openBusinessSite"
        />
        <Button
          label="התחבר לעסק"
          icon="pi pi-sign-in"
          :loading="impersonating"
          @click="loginAsBusiness"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center h-64">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <template v-if="data">
      <!-- Business Info Card -->
      <div class="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 mb-6 shadow-sm">
        <div class="flex items-center justify-between gap-3 mb-4">
          <h2 class="text-base md:text-lg font-semibold text-gray-900">פרטי העסק</h2>
          <div class="flex gap-2">
            <template v-if="!editMode">
              <Button
                icon="pi pi-users"
                label="ליד"
                size="small"
                outlined
                class="!rounded-xl"
                @click="goToLead"
              />
              <Button
                icon="pi pi-pencil"
                label="עריכה"
                size="small"
                outlined
                class="!rounded-xl"
                @click="enterEditMode"
              />
            </template>
            <template v-else>
              <Button
                label="ביטול"
                size="small"
                text
                class="!rounded-xl"
                :disabled="saving"
                @click="cancelEdit"
              />
              <Button
                icon="pi pi-check"
                label="שמירה"
                size="small"
                :loading="saving"
                class="!rounded-xl !bg-gradient-to-r !from-purple-600 !to-indigo-600 !text-white !border-0"
                @click="saveEdit"
              />
            </template>
          </div>
        </div>

        <!-- View mode -->
        <div v-if="!editMode" class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span class="text-gray-400">תוכנית</span>
            <div class="mt-1">
              <Tag
                :value="data.business.plan === 'premium' ? 'פרימיום' : 'חינמי'"
                :severity="data.business.plan === 'premium' ? 'success' : 'secondary'"
              />
            </div>
          </div>
          <div>
            <span class="text-gray-400">סטטוס חוזה</span>
            <div class="mt-1">
              <Tag
                :value="contractStatusLabel(data.business.contractStatus)"
                :severity="contractStatusSeverity(data.business.contractStatus)"
              />
            </div>
          </div>
          <div>
            <span class="text-gray-400">תחילת ניסיון</span>
            <div class="mt-1 font-medium text-gray-900">{{ formatDate(data.business.trialStartDate) }}</div>
          </div>
          <div>
            <span class="text-gray-400">סיום ניסיון</span>
            <div class="mt-1 font-medium text-gray-900">{{ formatDate(data.business.trialEndDate) }}</div>
          </div>
          <div>
            <span class="text-gray-400">מחיר חודשי</span>
            <div class="mt-1 font-medium text-gray-900">
              <template v-if="data.business.monthlyPrice">{{ data.business.monthlyPrice.toLocaleString('he-IL') }} ש"ח</template>
              <template v-else>—</template>
            </div>
          </div>
          <div>
            <span class="text-gray-400">נוצר</span>
            <div class="mt-1 font-medium text-gray-900">{{ formatDate(data.business.createdAt) }}</div>
          </div>
          <div>
            <span class="text-gray-400">שירותים</span>
            <div class="mt-1 font-medium text-gray-900">{{ data.business.servicesCount }}</div>
          </div>
          <div>
            <span class="text-gray-400">עובדים</span>
            <div class="mt-1 font-medium text-gray-900">{{ data.business.employeesCount }}</div>
          </div>
          <div>
            <span class="text-gray-400">WhatsApp</span>
            <div class="mt-1">
              <Tag
                :value="data.business.hasWhatsappIntegration ? 'מחובר' : 'לא מחובר'"
                :severity="data.business.hasWhatsappIntegration ? 'success' : 'secondary'"
              />
            </div>
          </div>
          <div class="col-span-2 md:col-span-4 border-t border-gray-100 mt-2 pt-3">
            <span class="text-gray-400">מגבלות הודעות</span>
            <div class="mt-1 flex flex-wrap gap-2 text-xs">
              <Tag :value="`SMS: ${data.messaging.sms.limit}`" severity="info" />
              <Tag :value="`WhatsApp: ${data.messaging.whatsapp.limit}`" severity="info" />
            </div>
          </div>
        </div>

        <!-- Edit mode -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div class="flex flex-col gap-1.5">
            <label class="text-gray-500 font-medium">תוכנית</label>
            <Select
              v-model="editForm.plan"
              :options="planOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              @change="applyPlanDefaults"
            />
            <span class="text-[11px] text-gray-400">שינוי תוכנית מעדכן את החוזה לברירות המחדל של התוכנית</span>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-gray-500 font-medium">סטטוס חוזה</label>
            <Select
              v-model="editForm.status"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-gray-500 font-medium">תחילת ניסיון</label>
            <DatePicker
              v-model="editForm.trialStartDate"
              dateFormat="dd/mm/yy"
              showIcon
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-gray-500 font-medium">סיום ניסיון</label>
            <DatePicker
              v-model="editForm.trialEndDate"
              dateFormat="dd/mm/yy"
              showIcon
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-gray-500 font-medium">מחיר חודשי (ש"ח) — משמש לחישוב MRR</label>
            <InputNumber v-model="editForm.monthlyPrice" :min="0" :step="10" class="w-full" />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-gray-500 font-medium">מגבלת SMS</label>
            <InputNumber v-model="editForm.smsLimit" :min="0" class="w-full" />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-gray-500 font-medium">מגבלת WhatsApp</label>
            <InputNumber v-model="editForm.whatsappLimit" :min="0" class="w-full" />
          </div>
          <div class="md:col-span-2 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
            <ToggleSwitch v-model="editForm.isTestBusiness" inputId="isTestBusiness" />
            <label for="isTestBusiness" class="text-sm text-amber-900 select-none cursor-pointer flex flex-col">
              <span class="font-medium">סמן כעסק בדיקה</span>
              <span class="text-xs text-amber-700">עסקי בדיקה יוסתרו כברירת מחדל מלוחות המחוונים</span>
            </label>
          </div>
          <div v-if="saveError" class="md:col-span-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-red-600 text-sm">
            {{ saveError }}
          </div>
        </div>
      </div>

      <!-- Appointments & Customers row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        <!-- Appointments -->
        <div class="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 shadow-sm">
          <h2 class="text-base md:text-lg font-semibold text-gray-900 mb-4">תורים</h2>

          <!-- Summary -->
          <div class="grid grid-cols-4 gap-3 mb-4">
            <div class="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
              <div class="text-xl md:text-2xl font-bold text-primary-600">{{ data.appointments.total }}</div>
              <div class="text-xs text-gray-400">סה"כ (שהתקיימו)</div>
            </div>
            <div class="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
              <div class="text-xl md:text-2xl font-bold text-blue-500">{{ data.appointments.last30d }}</div>
              <div class="text-xs text-gray-400">30 יום</div>
            </div>
            <div class="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
              <div class="text-xl md:text-2xl font-bold text-emerald-500">{{ data.appointments.last7d }}</div>
              <div class="text-xs text-gray-400">7 ימים</div>
            </div>
            <div class="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
              <div class="text-xl md:text-2xl font-bold text-teal-500">{{ data.appointments.upcoming ?? 0 }}</div>
              <div class="text-xs text-gray-400">עתידיים</div>
            </div>
          </div>

          <!-- By status -->
          <div class="mb-4">
            <h3 class="text-sm font-medium text-gray-500 mb-2">לפי סטטוס</h3>
            <div class="flex flex-wrap gap-2">
              <Tag
                v-for="(count, status) of data.appointments.byStatus"
                :key="status"
                :value="`${appointmentStatusLabel(status)}: ${count}`"
                :severity="appointmentStatusSeverity(status)"
              />
            </div>
          </div>

          <!-- By source -->
          <div>
            <h3 class="text-sm font-medium text-gray-500 mb-2">לפי מקור</h3>
            <div class="flex flex-wrap gap-2">
              <Tag
                v-for="(count, source) of data.appointments.bySource"
                :key="source"
                :value="`${appointmentSourceLabel(source)}: ${count}`"
                severity="info"
              />
            </div>
          </div>

          <!-- Chart -->
          <div class="mt-4">
            <h3 class="text-sm font-medium text-gray-500 mb-2">תורים ליום (30 יום)</h3>
            <Chart type="bar" :data="appointmentsChartData" :options="chartOptions" />
          </div>
        </div>

        <!-- Customers & Messaging -->
        <div class="space-y-4 md:space-y-6">
          <!-- Customers -->
          <div class="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 shadow-sm">
            <h2 class="text-base md:text-lg font-semibold text-gray-900 mb-4">לקוחות</h2>
            <div class="grid grid-cols-3 gap-3">
              <div class="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                <div class="text-xl md:text-2xl font-bold text-primary-600">{{ data.customers.total }}</div>
                <div class="text-xs text-gray-400">סה"כ</div>
              </div>
              <div class="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                <div class="text-xl md:text-2xl font-bold text-emerald-500">{{ data.customers.new30d }}</div>
                <div class="text-xs text-gray-400">חדשים (30י)</div>
              </div>
              <div class="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                <div class="text-xl md:text-2xl font-bold text-blue-500">{{ data.customers.engagementRate }}%</div>
                <div class="text-xs text-gray-400">אחוז מעורבות</div>
              </div>
            </div>
          </div>

          <!-- Messaging -->
          <div class="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 shadow-sm">
            <h2 class="text-base md:text-lg font-semibold text-gray-900 mb-4">הודעות (חודש נוכחי)</h2>
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">SMS</span>
                <span class="text-sm font-medium text-gray-900">
                  {{ data.messaging.sms.used }} / {{ data.messaging.sms.limit }}
                </span>
              </div>
              <ProgressBar
                :value="data.messaging.sms.limit > 0 ? Math.round((data.messaging.sms.used / data.messaging.sms.limit) * 100) : 0"
                :showValue="false"
                style="height: 8px"
              />
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">WhatsApp</span>
                <span class="text-sm font-medium text-gray-900">
                  {{ data.messaging.whatsapp.used }} / {{ data.messaging.whatsapp.limit }}
                </span>
              </div>
              <ProgressBar
                :value="data.messaging.whatsapp.limit > 0 ? Math.round((data.messaging.whatsapp.used / data.messaging.whatsapp.limit) * 100) : 0"
                :showValue="false"
                style="height: 8px"
                :pt="{ value: { class: '!bg-teal-500' } }"
              />
              <div class="flex items-center justify-between mt-2">
                <span class="text-sm text-gray-500">AI Tokens</span>
                <span class="text-sm font-medium text-gray-900">{{ data.messaging.aiTokens.toLocaleString() }}</span>
              </div>
            </div>
          </div>

          <!-- Revenue -->
          <div class="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 shadow-sm">
            <h2 class="text-base md:text-lg font-semibold text-gray-900 mb-2">הכנסות משוערות ללקוח (30 יום)</h2>
            <div class="text-2xl md:text-3xl font-bold bg-gradient-to-l from-purple-500 to-indigo-500 bg-clip-text text-transparent">
              {{ data.estimatedRevenue30d.toLocaleString('he-IL') }} ש"ח
            </div>
            <div v-if="data.estimatedUpcomingRevenue" class="text-sm text-gray-500 mt-2">
              צפי מתורים עתידיים:
              <span class="font-semibold text-teal-600">{{ data.estimatedUpcomingRevenue.toLocaleString('he-IL') }} ש"ח</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Services -->
      <div class="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 overflow-x-auto shadow-sm">
        <h2 class="text-base md:text-lg font-semibold text-gray-900 mb-4">שירותים</h2>
        <DataTable :value="data.services" :rows="10" class="!text-sm">
          <Column field="name" header="שם השירות" sortable />
          <Column field="price" header="מחיר" sortable>
            <template #body="{ data }">
              <span class="text-gray-900">{{ data.price }} ש"ח</span>
            </template>
          </Column>
          <Column field="appointmentCount" header="תורים (30 יום)" sortable>
            <template #body="{ data }">
              <span class="font-semibold text-primary-600">{{ data.appointmentCount }}</span>
            </template>
          </Column>
        </DataTable>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import { ContractStatus, Plans, limitsByPlan, type IPlatformBusinessDeepDive } from '@torim-app/types';
import { ActivityApi } from '../api/activity-api';
import { ImpersonationApi, dashboardBaseUrl, businessSiteUrl } from '../api/impersonation-api';
import { PlatformRouteNames } from '../router/route-names';
import { useNotify } from '../composables/useNotify';

const { notifySuccess, notifyError } = useNotify();
const props = defineProps<{ bid: string }>();
const router = useRouter();
const loading = ref(false);
const data = ref<IPlatformBusinessDeepDive | null>(null);
const impersonating = ref(false);

// Swap the platform-admin session for the business owner's, then land in the
// business dashboard. Exit is handled by the impersonation banner there.
const loginAsBusiness = async () => {
  if (impersonating.value) return;
  impersonating.value = true;
  try {
    await ImpersonationApi.impersonate(props.bid);
    window.location.assign(`${dashboardBaseUrl()}/admin`);
  } catch (e) {
    impersonating.value = false;
    notifyError('ההתחברות לעסק נכשלה');
  }
};

const openBusinessSite = () => {
  window.open(businessSiteUrl(props.bid), '_blank', 'noopener');
};

const editMode = ref(false);
const saving = ref(false);
const saveError = ref('');

interface EditForm {
  plan: Plans;
  status: ContractStatus;
  trialStartDate: Date | null;
  trialEndDate: Date | null;
  monthlyPrice: number | null;
  smsLimit: number;
  whatsappLimit: number;
  isTestBusiness: boolean;
}

const editForm = reactive<EditForm>({
  plan: Plans.FREE,
  status: ContractStatus.Trial,
  trialStartDate: null,
  trialEndDate: null,
  monthlyPrice: null,
  smsLimit: 0,
  whatsappLimit: 0,
  isTestBusiness: false,
});

const planOptions = [
  { label: 'פרימיום', value: Plans.PREMIUM },
  { label: 'חינמי', value: Plans.FREE },
];

const statusOptions = [
  { label: 'ניסיון', value: ContractStatus.Trial },
  { label: 'פעיל', value: ContractStatus.Active },
  { label: 'פג תוקף', value: ContractStatus.Expired },
];

const enterEditMode = () => {
  if (!data.value) return;
  const b = data.value.business;
  editForm.plan = (b.plan as Plans) || Plans.FREE;
  editForm.status = (b.contractStatus as ContractStatus) || ContractStatus.Trial;
  editForm.trialStartDate = b.trialStartDate ? new Date(b.trialStartDate) : null;
  editForm.trialEndDate = b.trialEndDate ? new Date(b.trialEndDate) : null;
  editForm.monthlyPrice = b.monthlyPrice ?? null;
  editForm.smsLimit = data.value.messaging.sms.limit || 0;
  editForm.whatsappLimit = data.value.messaging.whatsapp.limit || 0;
  editForm.isTestBusiness = !!b.isTestBusiness;
  saveError.value = '';
  editMode.value = true;
};

// When the plan is switched, reset the contract fields to that plan's defaults
// (mirrors the backend). The admin can still tweak any field before saving.
const applyPlanDefaults = () => {
  const limits = limitsByPlan[editForm.plan];
  editForm.smsLimit = limits.sms;
  editForm.whatsappLimit = limits.whatsapp;
  if (editForm.plan === Plans.FREE) {
    editForm.status = ContractStatus.Active;
    editForm.monthlyPrice = 0;
  } else {
    editForm.status = ContractStatus.Trial;
    editForm.trialStartDate = DateTime.local().toJSDate();
    editForm.trialEndDate = DateTime.local().endOf('day').plus({ days: 14 }).toJSDate();
  }
};

const cancelEdit = () => {
  editMode.value = false;
  saveError.value = '';
};

const saveEdit = async () => {
  saving.value = true;
  saveError.value = '';
  try {
    const updated = await ActivityApi.updateBusiness(props.bid, {
      plan: editForm.plan,
      isTestBusiness: editForm.isTestBusiness,
      contract: {
        status: editForm.status,
        trialStartDate: editForm.trialStartDate ? editForm.trialStartDate.toISOString() : null,
        trialEndDate: editForm.trialEndDate ? editForm.trialEndDate.toISOString() : null,
        monthlyPrice: editForm.monthlyPrice,
        limits: {
          sms: editForm.smsLimit ?? 0,
          whatsapp: editForm.whatsappLimit ?? 0,
        },
      },
    });
    data.value = updated;
    editMode.value = false;
    notifySuccess('העסק עודכן בהצלחה');
  } catch (e) {
    console.error('Failed to update business:', e);
    saveError.value = 'שמירה נכשלה, נסה שוב';
  } finally {
    saving.value = false;
  }
};

const formatDate = (date: string | undefined) => {
  if (!date) return '-';
  return DateTime.fromJSDate(new Date(date)).toFormat('dd/MM/yyyy');
};

const contractStatusLabel = (status: string) => {
  const map: Record<string, string> = { trial: 'ניסיון', active: 'פעיל', expired: 'פג תוקף' };
  return map[status] || status;
};

const contractStatusSeverity = (status: string) => {
  const map: Record<string, string> = { trial: 'warn', active: 'success', expired: 'danger' };
  return map[status] || 'secondary';
};

const appointmentStatusLabel = (status: string) => {
  const map: Record<string, string> = {
    verified: 'מאושר',
    approved: 'אושר ע"י לקוח',
    cancelled: 'בוטל',
    finished: 'הסתיים',
    'not-verified': 'לא מאומת',
    'waiting-for-business-approval': 'ממתין לאישור',
  };
  return map[status] || status;
};

const appointmentStatusSeverity = (status: string) => {
  const map: Record<string, string> = {
    verified: 'success',
    approved: 'info',
    cancelled: 'danger',
    finished: 'secondary',
  };
  return map[status] || 'secondary';
};

const appointmentSourceLabel = (source: string) => {
  const map: Record<string, string> = {
    business_site: 'אתר העסק',
    admin: 'ניהול ידני',
    whatsapp_bot: 'בוט WhatsApp',
    admin_bot: 'בוט ניהול',
    unknown: 'לא ידוע',
  };
  return map[source] || source;
};

const appointmentsChartData = computed(() => {
  if (!data.value) return { labels: [], datasets: [] };
  return {
    labels: data.value.appointments.perDay.map((d) =>
      DateTime.fromISO(d.date).toFormat('dd/MM')
    ),
    datasets: [
      {
        label: 'תורים',
        data: data.value.appointments.perDay.map((d) => d.count),
        backgroundColor: 'rgba(136, 100, 238, 0.6)',
        borderColor: '#8864ee',
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: { display: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { stepSize: 1, color: '#9ca3af' },
      grid: { color: 'rgba(0, 0, 0, 0.05)' },
      border: { color: 'rgba(0, 0, 0, 0.1)' },
    },
    x: {
      ticks: { maxTicksLimit: 10, font: { size: 10 }, color: '#9ca3af' },
      grid: { display: false },
      border: { color: 'rgba(0, 0, 0, 0.1)' },
    },
  },
};

const fetchData = async () => {
  loading.value = true;
  try {
    data.value = await ActivityApi.fetchBusinessDeepDive(props.bid);
  } catch (e) {
    console.error('Failed to fetch business data:', e);
    notifyError('טעינת נתוני העסק נכשלה');
  } finally {
    loading.value = false;
  }
};

const goToLead = () => {
  router.push({
    name: PlatformRouteNames.Crm,
    query: { bid: props.bid },
  });
};

onMounted(fetchData);
</script>
